/**
 * Conversation Sidebar Component
 * Multi-Chat System: Sidebar for managing conversations
 */

import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Fade,
  Tooltip,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Forum as ForumIcon,
  Close as CloseIcon,
  Checklist as SelectIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import {
  exportConversation,
  exportConversations,
  type ExportFormat,
} from '../../utils/chat/exportConversation';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useDialogState } from '@/hooks/ui/useDialogState';
import { useSelectionMode } from '@/hooks/ui/useSelectionMode';
import { log } from '../../utils/errors/logger';
import {
  useGetConversations,
  useConversationSearch,
  useCreateConversation,
  useDeleteConversation,
  useUpdateConversation,
  useDeleteConversationsBatch,
} from '../../hooks/api/useConversations';
import { ConversationManagementDialog } from './ConversationManagementDialog';
import { ConversationList } from './ConversationList';
import { DeleteConfirmationDialog } from '../ui/DeleteConfirmationDialog';

interface ConversationSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface ConversationDialogItem {
  id: string;
  title: string;
}

const ConversationSidebarInner: React.FC<ConversationSidebarProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const dialogState = useDialogState<ConversationDialogItem>();
  const navigate = useNavigate();

  // Get current conversation ID from URL
  const { conversationId: currentConversationId } = useParams<{ conversationId?: string }>();

  // API hooks
  const { data: conversations = [], isLoading, error } = useGetConversations();
  const createConversationMutation = useCreateConversation();
  const deleteConversationMutation = useDeleteConversation();
  const updateConversationMutation = useUpdateConversation();
  const deleteConversationsBatchMutation = useDeleteConversationsBatch();
  const [searchQuery, setSearchQuery] = useState('');

  // The server searches what was said in a conversation; the list in memory
  // only knows the title and the preview of the last message. Typing filters
  // that list at once, and the server's hits join a moment later.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);
  const { data: contentHits, isFetching: isSearchingContent } = useConversationSearch(debouncedSearch);

  const filteredConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {return conversations;}

    const local = conversations.filter((conversation) => {
      const title = conversation.title?.toLowerCase() ?? '';
      const preview = conversation.last_message_preview?.toLowerCase() ?? '';
      return title.includes(query) || preview.includes(query);
    });
    // Hits of an older query must not show under a newer one.
    const serverIsCurrent = debouncedSearch.trim().toLowerCase() === query;
    if (!serverIsCurrent || !contentHits) {return local;}

    const seen = new Set(local.map((conversation) => conversation.id));
    const merged = [...local, ...contentHits.filter((hit) => !seen.has(hit.id))];
    return merged.sort((a, b) => String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? '')));
  }, [conversations, searchQuery, debouncedSearch, contentHits]);

  // Selection mode state
  const {
    isSelectionMode,
    selectedIds,
    toggleSelectionMode,
    exitSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    selectionCount,
  } = useSelectionMode<string>();

  // Which conversations a pending export covers: a single id from the row
  // hover, or null for "everything currently selected". The format menu needs
  // to know this before the user has picked md or txt.
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exportTarget, setExportTarget] = useState<string | null>(null);

  const openExportMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, conversationId: string | null) => {
      setExportTarget(conversationId);
      setExportAnchor(event.currentTarget);
    },
    []
  );

  const closeExportMenu = useCallback(() => {
    setExportAnchor(null);
    setExportTarget(null);
  }, []);

  const handleExportFormat = useCallback(
    async (format: ExportFormat) => {
      const ids = exportTarget ? [exportTarget] : [...selectedIds];
      closeExportMenu();
      if (ids.length === 0) {
        return;
      }
      try {
        if (ids.length === 1) {
          exportConversation(ids[0], format);
        } else {
          await exportConversations(ids, format);
        }
      } catch (error) {
        log.error('Conversation export failed', error);
      }
    },
    [exportTarget, selectedIds, closeExportMenu]
  );

  // The row hands over the button it was clicked on, so the menu opens right
  // there instead of somewhere off the list.
  const handleRowExport = useCallback((conversationId: string, anchor: HTMLElement) => {
    setExportTarget(conversationId);
    setExportAnchor(anchor);
  }, []);

  // Event handlers
  const handleCreateConversation = async () => {
    try {
      log.user.action('new_conversation_click', 'ConversationSidebar');

      const newConversation = await createConversationMutation.mutateAsync(undefined);

      // Navigate to the new conversation
      void navigate(`/chat/${newConversation.id}`);

      showSuccess(t('conversation.created', { title: newConversation.title }));

      log.user.action('conversation_created_success', 'ConversationSidebar', {
        conversationId: newConversation.id,
        title: newConversation.title,
      });
    } catch (error) {
      log.component.error(
        'ConversationSidebar',
        error instanceof Error ? error : new Error('Failed to create conversation')
      );
      showError(t('conversation.createFailed'));
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId !== currentConversationId) {
      // Navigate to the selected conversation
      void navigate(`/chat/${conversationId}`);
      log.user.action('conversation_selected', 'ConversationSidebar', { conversationId });
    }

    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const handleDeleteConversation = (conversationId: string, title: string) => {
    dialogState.openDeleteConfirm({ id: conversationId, title });
  };

  const handleConfirmDelete = async () => {
    if (!dialogState.itemToDelete) {return;}

    const { id: conversationId, title } = dialogState.itemToDelete;

    try {
      await deleteConversationMutation.mutateAsync(conversationId);

      // If we deleted the current conversation, navigate to chat home
      if (conversationId === currentConversationId) {
        void navigate('/chat', { replace: true });
      }

      showSuccess(t('conversation.deleted', { title }));
      dialogState.closeDeleteConfirm();

      log.user.action('conversation_deleted_success', 'ConversationSidebar', {
        conversationId,
        title,
      });
    } catch (error) {
      log.component.error(
        'ConversationSidebar',
        error instanceof Error ? error : new Error('Failed to delete conversation')
      );
      showError(t('conversation.deleteFailed'));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectionCount === 0) {return;}

    // Open delete confirmation with custom message for bulk delete
    dialogState.openDeleteConfirm({
      id: 'bulk',
      title: t('conversation.bulkDelete.confirmTitle', { count: selectionCount }),
    });
  };

  const handleConfirmBulkDelete = async () => {
    if (selectionCount === 0) {return;}

    const conversationIdsToDelete: string[] = Array.from(selectedIds);

    try {
      const result = await deleteConversationsBatchMutation.mutateAsync(
        conversationIdsToDelete
      );

      // If current conversation was deleted, navigate to chat home
      if (currentConversationId && result.deleted.includes(currentConversationId)) {
        void navigate('/chat', { replace: true });
      }

      // Exit selection mode and clear selections
      exitSelectionMode();

      // Show appropriate success message
      if (result.failed.length === 0) {
        showSuccess(
          t('conversation.bulkDelete.success', { count: result.deleted.length })
        );
      } else {
        showSuccess(
          t('conversation.bulkDelete.partialSuccess', {
            success: result.deleted.length,
            total: conversationIdsToDelete.length,
          })
        );
      }

      dialogState.closeDeleteConfirm();

      log.user.action('conversations_bulk_deleted_success', 'ConversationSidebar', {
        deleted: result.deleted.length,
        failed: result.failed.length,
      });
    } catch (error) {
      log.component.error(
        'ConversationSidebar',
        error instanceof Error ? error : new Error('Failed to bulk delete conversations')
      );
      showError(t('conversation.deleteFailed'));
    }
  };

  const handleSelectAll = () => {
    const allConversationIds = filteredConversations.map((c) => c.id);
    if (allConversationIds.length > 0 && allConversationIds.every((id) => selectedIds.has(id))) {
      clearSelection();
    } else {
      selectAll(allConversationIds);
    }
  };

  const handleEditConversation = (conversation: ConversationDialogItem) => {
    dialogState.openEditDialog(conversation);
  };

  const handleDialogSubmit = async (data: { title: string }) => {
    if (dialogState.isEditing && dialogState.editingItem) {
      try {
        await updateConversationMutation.mutateAsync({
          conversationId: dialogState.editingItem.id,
          updates: { title: data.title },
        });

        showSuccess(t('conversation.updated'));
        dialogState.closeDialog();

        log.user.action('conversation_updated_success', 'ConversationSidebar', {
          conversationId: dialogState.editingItem.id,
          newTitle: data.title,
        });
      } catch (error) {
        log.component.error(
          'ConversationSidebar',
          error instanceof Error ? error : new Error('Failed to update conversation')
        );
        showError(t('conversation.updateFailed'));
      }
    }
  };

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) {return;}

      // Escape - Exit selection mode or close sidebar
      if (e.key === 'Escape') {
        e.preventDefault();
        if (isSelectionMode) {
          exitSelectionMode();
        } else {
          onClose();
        }
        return;
      }

      // N - New conversation (only when not in selection mode)
      if ((e.key === 'n' || e.key === 'N') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const target = e.target as HTMLElement;
        // Don't trigger if user is typing in an input or in selection mode
        if (
          !isSelectionMode &&
          target.tagName !== 'INPUT' &&
          target.tagName !== 'TEXTAREA'
        ) {
          e.preventDefault();
          void handleCreateConversation();
        }
      }
    },
    [open, onClose, isSelectionMode, exitSelectionMode]
  );

  // Setup keyboard shortcuts
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Component logging
  useEffect(() => {
    if (open) {
      log.component.mount('ConversationSidebar', { conversationCount: conversations.length });
    }
  }, [open, conversations.length]);

  const handleConfirmDeleteAction = () => {
    if (dialogState.itemToDelete?.id === 'bulk') {
      void handleConfirmBulkDelete();
      return;
    }
    void handleConfirmDelete();
  };

  return (
    <Fade in={open} timeout={250}>
      <Box
        role="complementary"
        aria-label={t('conversation.sidebar')}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          {!isSelectionMode ? (
            // Normal mode header
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ForumIcon color="primary" sx={{ fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    {t('conversation.conversations')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Selection mode toggle - more visible */}
                  {conversations.length > 0 && (
                    <Tooltip title={t('conversation.bulkDelete.select')} arrow>
                      <Button
                        size="small"
                        variant="text"
                        onClick={toggleSelectionMode}
                        startIcon={<SelectIcon />}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': {
                            color: 'primary.main',
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        {t('conversation.bulkDelete.select')}
                      </Button>
                    </Tooltip>
                  )}

                  <Tooltip title={`${t('common.close')} (Esc)`} arrow>
                    <IconButton
                      onClick={onClose}
                      aria-label={t('common.close')}
                      size="small"
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'rotate(90deg)',
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* New Conversation button */}
              <Tooltip title={`${t('conversation.new')} (N)`} arrow placement="bottom">
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={handleCreateConversation}
                  disabled={createConversationMutation.isPending}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {t('conversation.new')}
                </Button>
              </Tooltip>

              <TextField
                fullWidth
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('conversation.searchPlaceholder', { defaultValue: 'Search conversations' })}
                sx={{ mt: 1.25 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery('')}
                          aria-label={t('conversation.clearSearch', { defaultValue: 'Clear search' })}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
              {searchQuery && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {isSearchingContent
                    // "0 of 3" must not read as the answer while the contents
                    // are still being searched.
                    ? t('conversation.searchingContent', { defaultValue: 'Searching the contents ...' })
                    : t('conversation.searchResults', {
                      count: filteredConversations.length,
                      total: conversations.length,
                      defaultValue: '{{count}} of {{total}} conversations'
                    })}
                </Typography>
              )}
            </>
          ) : (
            // Selection mode header - cleaner layout
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                {/* Cancel button */}
                <Button
                  size="small"
                  variant="text"
                  onClick={exitSelectionMode}
                  sx={{
                    textTransform: 'none',
                    color: 'text.secondary',
                    minWidth: 'auto',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {t('conversation.bulkDelete.cancelSelection')}
                </Button>

                {/* Selection count */}
                <Chip
                  label={t('conversation.bulkDelete.selected', { count: selectionCount })}
                  size="small"
                  color={selectionCount > 0 ? 'primary' : 'default'}
                  variant={selectionCount > 0 ? 'filled' : 'outlined'}
                />

                {/* Export button */}
                <Tooltip title={t('conversation.export.exportSelected', { count: selectionCount })} arrow>
                  <span>
                    <IconButton
                      onClick={(event) => {
                        openExportMenu(event, null);
                      }}
                      disabled={selectionCount === 0}
                      sx={{
                        color: 'text.secondary',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          color: 'primary.main',
                        },
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Delete button */}
                <Tooltip title={t('conversation.bulkDelete.deleteSelected', { count: selectionCount })} arrow>
                  <span>
                    <IconButton
                      onClick={handleBulkDeleteClick}
                      disabled={selectionCount === 0 || deleteConversationsBatchMutation.isPending}
                      color="error"
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: 'error.lighter',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              {/* Select All row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <Checkbox
                  checked={
                    filteredConversations.length > 0 &&
                    filteredConversations.every((conversation) => selectedIds.has(conversation.id))
                  }
                  indeterminate={
                    filteredConversations.some((conversation) => selectedIds.has(conversation.id)) &&
                    !filteredConversations.every((conversation) => selectedIds.has(conversation.id))
                  }
                  onChange={handleSelectAll}
                  size="small"
                  color="primary"
                />
                <Typography variant="body2" color="text.secondary">
                  {t('conversation.bulkDelete.selectAll')}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  ({filteredConversations.length})
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Divider />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ConversationList
            conversations={filteredConversations}
            currentConversationId={currentConversationId}
            isLoading={isLoading}
            error={error}
            emptyTitle={
              searchQuery
                ? t('conversation.noSearchResults', { defaultValue: 'No matching conversations' })
                : undefined
            }
            emptyDescription={
              searchQuery
                ? t('conversation.noSearchResultsHint', { defaultValue: 'Try another keyword or clear your search.' })
                : undefined
            }
            onSelect={handleSelectConversation}
            onDelete={handleDeleteConversation}
            onEdit={handleEditConversation}
            onExport={handleRowExport}
            selectionMode={isSelectionMode}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
          />
        </Box>

        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={closeExportMenu}
        >
          <MenuItem
            onClick={() => {
              void handleExportFormat('md');
            }}
          >
            {t('conversation.export.asMarkdown')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              void handleExportFormat('txt');
            }}
          >
            {t('conversation.export.asText')}
          </MenuItem>
        </Menu>

        {/* Footer hint */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <kbd style={{
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'currentColor',
              fontSize: '0.75rem',
              fontFamily: 'monospace'
            }}>
              N
            </kbd>
            {t('conversation.newShortcut')} •
            <kbd style={{
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'currentColor',
              fontSize: '0.75rem',
              fontFamily: 'monospace'
            }}>
              Esc
            </kbd>
            {t('common.close')}
          </Typography>
        </Box>

        {/* Management Dialog */}
        <ConversationManagementDialog
          open={dialogState.dialogOpen}
          onClose={dialogState.closeDialog}
          conversation={dialogState.editingItem ?? undefined}
          mode="edit"
          onSubmit={(data) => {
            void handleDialogSubmit(data as { title: string });
          }}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={dialogState.deleteConfirmOpen}
          onClose={dialogState.closeDeleteConfirm}
          onConfirm={handleConfirmDeleteAction}
          title={
            dialogState.itemToDelete?.id === 'bulk'
              ? dialogState.itemToDelete.title
              : undefined
          }
          itemName={
            dialogState.itemToDelete?.id === 'bulk'
              ? undefined
              : dialogState.itemToDelete?.title
          }
          message={
            dialogState.itemToDelete?.id === 'bulk'
              ? t('conversation.bulkDelete.confirmMessage', { count: selectionCount })
              : undefined
          }
          loading={
            dialogState.itemToDelete?.id === 'bulk'
              ? deleteConversationsBatchMutation.isPending
              : deleteConversationMutation.isPending
          }
        />
      </Box>
    </Fade>
  );
};

/**
 * Memoized: the sidebar takes only { open, onClose } (stable when the parent
 * passes a useCallback onClose), so it should not re-render when the chat page
 * re-renders on every keystroke. Its internal conversation list/filtering is
 * already useMemo-gated; this stops the whole subtree re-rendering needlessly.
 */
export const ConversationSidebar = React.memo(ConversationSidebarInner);
